import * as actionTypes from "./actionTypes";
import {
    getMyPeopleScheduleAPI,
    createMyPeopleScheduleAPI,
    getMyPeopleScheduleExtraHoursAPI,
} from "@icarius-connection/api";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import { startOfDay, endOfDay, addHours, addMinutes, parse, format } from "date-fns";

const errorHandler = (e, dispatch) => {
    let errorString = "";
    let duration = 5000;
    switch (e.response?.data?.status) {
        case "INVALID_DATA":
        case "VALIDATION_ERROR":
            errorString = getLocalizedString("validationError");
            break;
        case "INVALID_MAX_WEEK_HOURS":
            errorString = getLocalizedString("scheduleChangeNotAllowedDueToExcessHours");
            duration = 15000;
            break;
        default:
            errorString = getErrorStringFromServerError(e);
            break;
    }
    dispatch(openSnackbarAction({ msg: errorString, severity: "error", duration: duration }));
};

const formatEventForCalendar = (resource, oldData, dateFormatUpperCase, isMyPeopleSchedule = false) => {
    let data = JSON.parse(JSON.stringify(oldData));
    let dateData = parse(data["Fecha"], dateFormatUpperCase.replace(/DD/g, "dd").replace(/YYYY/g, "yyyy"), new Date());
    const startDate = formatStartDate(data, dateData);
    const endDate = formatEndDate(data, new Date(startDate));

    delete data["Fecha"];
    delete data["Hora de entrada"];
    delete data["Hora de salida"];
    delete data["Horas totales"];

    if (isMyPeopleSchedule) {
        delete data["tooltipText"];
        data.start = format(startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS");
        data.end = format(endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS");
    } else {
        data.start = startDate.toISOString();
        data.end = endDate.toISOString();
    }
    data.resource = resource;

    return data;
};

const formatStartDate = (data, dateData) => {
    const hours = Math.floor(parseInt(data["Hora de entrada"]) / 100);
    const minutes = parseInt(data["Hora de entrada"]) % 100;
    if (hours === 0 && minutes === 0) {
        return startOfDay(dateData);
    } else {
        const d = new Date(dateData);
        d.setHours(hours, minutes, 0, 0);
        return d;
    }
};

const formatEndDate = (data, dateData) => {
    const addHoursVal = Math.floor(parseInt(data["Horas totales"]) / 100);
    const addMinutesVal = parseInt(data["Horas totales"]) % 100;
    if (addHoursVal === 0 && addMinutesVal === 0) {
        return endOfDay(dateData);
    } else {
        return addMinutes(addHours(dateData, addHoursVal), addMinutesVal);
    }
};

const processCollaboratorShifts = (scheduleData, initialShifts) => {
    const {
        myEmployeesCodes,
        getScheduleData,
        dateFormatUpperCase,
        getScheduleExceptionsData,
        getScheduleRequestsData,
        getScheduleColaborators,
        getScheduleHolidaysData,
    } = scheduleData;

    let shifts = [...initialShifts];

    getScheduleColaborators.forEach((collaborator) => {
        const { resource, shift } = collaborator;

        if (myEmployeesCodes.includes(resource)) {
            const collaboratorScheduleData = getScheduleData.filter((item) => item["shift"] === shift);
            const collaboratorHolidays = getScheduleHolidaysData.filter((item) => item["resource"] === resource);
            const collaboratorExceptions = getScheduleExceptionsData.filter((item) => item["resource"] === resource);
            const collaboratorPendingRequests = getScheduleRequestsData.filter((item) => item["resource"] === resource);

            let tempHoliday = [];

            collaboratorScheduleData.forEach((data) => {
                const hasException = collaboratorExceptions.find((item) => item["Fecha"] === data["Fecha"]);
                const hasHoliday = collaboratorHolidays.find((item) => item["start"] === data["Fecha"]);
                const hasPendingRequest = collaboratorPendingRequests.find((item) => item["Fecha"] === data["Fecha"]);

                const isHolidayRow = hasHoliday && data.scheduleId === "FERIADO";
                const baseEvent = isHolidayRow
                    ? { ...data, title: hasHoliday.title, shortTitle: hasHoliday.title }
                    : data;
                const showBase =
                    (!hasException || hasPendingRequest || hasHoliday || data.type === "free") &&
                    !(hasException && data.type === "free");

                if (showBase) shifts.push(formatEventForCalendar(resource, baseEvent, dateFormatUpperCase, true));
                if (isHolidayRow) tempHoliday.push(baseEvent);
                if (hasPendingRequest)
                    shifts.push(
                        formatEventForCalendar(
                            resource,
                            { ...hasPendingRequest, type: "request" },
                            dateFormatUpperCase,
                            true
                        )
                    );
                if (hasException)
                    shifts.push(
                        formatEventForCalendar(
                            resource,
                            { ...hasException, type: "exception" },
                            dateFormatUpperCase,
                            true
                        )
                    );
            });

            const dateArray = new Set(tempHoliday.map((item) => item.Fecha));
            const filteredHolidays = collaboratorHolidays.filter((item) => !dateArray.has(item.start));

            if (tempHoliday.length !== collaboratorHolidays.length) {
                filteredHolidays.forEach((item) =>
                    shifts.push(
                        formatEventForCalendar(
                            resource,
                            {
                                Fecha: item.start,
                                "Hora de entrada": 0,
                                "Hora de salida": 0,
                                "Horas totales": 0,
                                shortTitle: item.title,
                                title: item.title,
                                type: "holiday",
                                tooltipText: "",
                                scheduleId: "FERIADO",
                            },
                            dateFormatUpperCase,
                            true
                        )
                    )
                );
            }
        }
    });

    const holidayArray = shifts.filter((item) => item["type"] === "holiday");
    const nonHolidayArray = shifts.filter((item) => item["type"] !== "holiday");
    return [...nonHolidayArray, ...holidayArray];
};

export const getMyPeopleScheduleAction = (employeeCode) => async (dispatch) => {
    dispatch({ type: actionTypes.GET });
    try {
        let response = await getMyPeopleScheduleAPI(false ? `?employeeCode=${employeeCode}` : "");
        let workplaces = response?.data?.data.workplaces;
        let turns = response?.data?.data.turns;
        let employees = response?.data?.data.employees;
        let shifts = response?.data?.data.shifts;
        let schedules = response?.data?.data.schedules;
        let openCalendars = response?.data?.data.openCalendars;
        let status = response?.data?.status;

        if (status !== "OK") {
            dispatch({ type: actionTypes.GET_REJECTED });
            dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
            return;
        }

        shifts = processCollaboratorShifts(response.data.data.scheduleData, shifts);

        dispatch({
            type: actionTypes.GET_FULFILLED,
            payload: { workplaces, turns, employees, shifts, schedules, openCalendars },
        });

        return response;
    } catch (e) {
        dispatch({ type: actionTypes.GET_REJECTED, payload: e });
        errorHandler(e, dispatch);
    }
};

export const getMyPeopleScheduleExtraHoursAction = (employeeCode, date) => async (dispatch) => {
    dispatch({ type: actionTypes.GET_EXTRA_HOURS_DATA });
    try {
        let response = await getMyPeopleScheduleExtraHoursAPI(`?employeeCode=${employeeCode}&date=${date}`);
        let status = response?.data?.status;

        if (status !== "OK") {
            dispatch({ type: actionTypes.GET_EXTRA_HOURS_DATA_REJECTED });
            dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
            return;
        }

        dispatch({
            type: actionTypes.GET_EXTRA_HOURS_DATA_FULFILLED,
        });

        return response;
    } catch (e) {
        dispatch({ type: actionTypes.GET_EXTRA_HOURS_DATA_REJECTED, payload: e });
        errorHandler(e, dispatch);
    }
};

export const createMyPeopleScheduleAction = (dataToSend) => async (dispatch) => {
    dispatch({ type: actionTypes.CREATE });
    try {
        let response = await createMyPeopleScheduleAPI(dataToSend);
        let workplaces = response?.data?.data.workplaces;
        let turns = response?.data?.data.turns;
        let employees = response?.data?.data.employees;
        let shifts = response?.data?.data.shifts;
        let schedules = response?.data?.data.schedules;
        let openCalendars = response?.data?.data.openCalendars;
        let status = response?.data?.status;

        if (status !== "OK") {
            dispatch({ type: actionTypes.CREATE_REJECTED });
            dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
            return;
        }

        shifts = processCollaboratorShifts(response.data.data.scheduleData, shifts);

        dispatch({
            type: actionTypes.CREATE_FULFILLED,
            payload: { workplaces, turns, employees, shifts, schedules, openCalendars },
        });

        dispatch(openSnackbarAction({ msg: getLocalizedString("schedulesUpdatedSuccessfully"), severity: "success" }));
        return response;
    } catch (e) {
        dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
        errorHandler(e, dispatch);
    }
};
